// Header Component

import { useState } from 'react';
import { useMapStore, useUserStore } from '../../stores';
import { cn, formatRelativeTime } from '../../lib/utils';
import { 
  Menu, 
  Bell, 
  User, 
  LogOut, 
  Settings, 
  CreditCard, 
  HelpCircle,
  ChevronDown,
  Sun,
  Moon,
  Monitor,
  Sparkles,
  LayoutDashboard,
  Target,
  Users,
  DollarSign,
  BarChart3,
  Cog,
  Shield,
  X,
  Check,
  AlertTriangle,
  Info,
} from 'lucide-react';

function NavItem({ label, icon: Icon, href }: { label: string; icon: React.ComponentType<any>; href: string }) {
  return (
    <a
      href={href}
      className="flex items-center gap-3 px-4 py-2.5 text-sm text-slate-700 hover:bg-slate-100 hover:text-slate-900 transition-colors"
    >
      <Icon className="w-5 h-5" />
      {label}
    </a>
  );
}

interface HeaderProps {
  onMenuClick: () => void;
  onSearchClick: () => void;
  /** Opens the leads list — desktop sidebar on lg+, overlay drawer below it. */
  onLeadsClick: () => void;
}

export function Header({ onMenuClick, onSearchClick, onLeadsClick }: HeaderProps) {
  const { user, setUser, notifications, markNotificationRead, markAllNotificationsRead, unreadCount } = useUserStore();
  const { mapState, setMapState, sidebarOpen, controlsOpen, leads } = useMapStore();
  const [showUserMenu, setShowUserMenu] = useState(false);
  const [showNotifications, setShowNotifications] = useState(false);
  const [showThemeMenu, setShowThemeMenu] = useState(false);

  const handleSignOut = () => {
    setUser(null);
    setShowUserMenu(false);
  };

  const theme = mapState.style === 'dark' ? 'dark' : 'light';

  return (
    <header className="fixed top-0 left-0 right-0 h-16 bg-white/90 backdrop-blur-xl border-b border-slate-200/50 z-30 flex items-center justify-between px-4 lg:px-6">
      {/* Left Side */}
      <div className="flex items-center gap-4">
        {/* Opens the map controls bottom sheet at every breakpoint. It used to
            toggle the leads list, which now has its own button on the right. */}
        <button
          onClick={onMenuClick}
          className={cn(
            'p-2 rounded-lg transition-colors',
            controlsOpen
              ? 'bg-primary-50 text-primary-600'
              : 'text-slate-600 hover:bg-slate-100 hover:text-slate-900'
          )}
          aria-label="Map settings"
          aria-expanded={controlsOpen}
          title="Map settings"
        >
          <Menu className="w-6 h-6" />
        </button>

        {/* Logo */}
        <div className="flex items-center gap-2">
          <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-primary-500 to-primary-700 flex items-center justify-center">
            <Sparkles className="w-5 h-5 text-white" />
          </div>
          <span className="font-bold text-xl text-slate-900 hidden sm:block">LeadMap3D</span>
        </div>

        {/* Search Bar - Desktop */}
        <div className="hidden lg:block">
          <button
            onClick={onSearchClick}
            className="w-72 lg:w-96 px-4 py-2 rounded-lg bg-slate-100 text-slate-500 hover:bg-slate-200 transition-colors flex items-center gap-2"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
            </svg>
            <span>Search businesses, addresses...</span>
            <kbd className="ml-auto px-2 py-0.5 text-xs bg-white/50 rounded">⌘K</kbd>
          </button>
        </div>
      </div>

      {/* Right Side */}
      <div className="flex items-center gap-1">
        {/* Leads — the leads list was previously only reachable from the left
            (hamburger on mobile, an unlabelled dashboard icon on desktop), so
            there was nothing in the top-right corner. */}
        <button
          onClick={onLeadsClick}
          className={cn(
            'relative flex items-center gap-2 px-2 sm:px-3 py-2 rounded-lg transition-colors',
            sidebarOpen
              ? 'bg-primary-50 text-primary-600'
              : 'text-slate-600 hover:bg-slate-100 hover:text-slate-900'
          )}
          aria-label={sidebarOpen ? 'Hide leads list' : 'Show leads list'}
          aria-pressed={sidebarOpen}
          title="Leads"
        >
          <Target className="w-5 h-5 shrink-0" />
          <span className="hidden sm:inline text-sm font-medium">Leads</span>
          {leads.length > 0 && (
            <span className="ml-0.5 min-w-[1.25rem] h-5 px-1 bg-primary-500 text-white text-xs font-bold rounded-full flex items-center justify-center">
              {leads.length > 99 ? '99+' : leads.length}
            </span>
          )}
        </button>

        <div className="w-px h-6 bg-slate-200 mx-1" />

        {/* Theme Toggle */}
        <div className="relative">
          <button
            onClick={() => setShowThemeMenu(!showThemeMenu)}
            className="p-2 rounded-lg text-slate-500 hover:bg-slate-100 hover:text-slate-900 transition-colors"
            aria-label="Theme Options"
          >
            {theme === 'dark' ? <Moon className="w-5 h-5" /> : <Sun className="w-5 h-5" />}
          </button>
          {showThemeMenu && (
            <div className="absolute right-0 top-full mt-2 w-40 bg-white rounded-xl shadow-lg border border-slate-200 py-2 z-50 animate-scale-in">
              {['light', 'dark', 'streets', 'satellite'].map(t => (
                <button
                  key={t}
                  onClick={() => {
                    setMapState({ style: t as any });
                    setShowThemeMenu(false);
                  }}
                  className={cn(
                    'w-full px-4 py-2 text-left text-sm transition-colors flex items-center gap-2',
                    mapState.style === t ? 'bg-primary-50 text-primary-700' : 'text-slate-700 hover:bg-slate-100'
                  )}
                >
                  {t === 'light' && <Sun className="w-4 h-4" />}
                  {t === 'dark' && <Moon className="w-4 h-4" />}
                  {t === 'streets' && <Monitor className="w-4 h-4" />}
                  {t === 'satellite' && <MapPin className="w-4 h-4" />}
                  <span className="capitalize">{t}</span>
                  {mapState.style === t && <Check className="w-4 h-4 ml-auto" />}
                </button>
              ))}
            </div>
          )}
        </div>

        {/* Notifications */}
        <div className="relative">
          <button
            onClick={() => setShowNotifications(!showNotifications)}
            className={cn(
              'relative p-2 rounded-lg text-slate-500 hover:bg-slate-100 hover:text-slate-900 transition-colors',
              unreadCount > 0 && 'text-primary-600'
            )}
            aria-label="Notifications"
          >
            <Bell className="w-5 h-5" />
            {unreadCount > 0 && (
              <span className="absolute -top-1 -right-1 w-5 h-5 bg-red-500 text-white text-xs font-bold rounded-full flex items-center justify-center">
                {unreadCount > 9 ? '9+' : unreadCount}
              </span>
            )}
          </button>

          {showNotifications && (
            <div className="absolute right-0 top-full mt-2 w-80 bg-white rounded-xl shadow-xl border border-slate-200 z-50 animate-scale-in">
              <div className="p-4 border-b border-slate-200 flex items-center justify-between">
                <h3 className="font-semibold text-slate-900">Notifications</h3>
                {unreadCount > 0 && (
                  <button
                    onClick={markAllNotificationsRead}
                    className="text-sm text-primary-600 hover:text-primary-700"
                  >
                    Mark all read
                  </button>
                )}
              </div>
              <div className="max-h-96 overflow-y-auto">
                {notifications.length === 0 ? (
                  <div className="p-8 text-center text-slate-500">
                    <Bell className="w-12 h-12 mx-auto mb-3 text-slate-300" />
                    <p>No notifications</p>
                  </div>
                ) : (
                  notifications.slice(0, 10).map(notif => (
                    <button
                      key={notif.id}
                      onClick={() => {
                        markNotificationRead(notif.id);
                        if (notif.actionUrl) window.open(notif.actionUrl, '_blank');
                      }}
                      className={cn(
                        'w-full p-4 hover:bg-slate-50 transition-colors flex items-start gap-3 text-left border-b border-slate-100 last:border-0',
                        !notif.read && 'bg-primary-50/50'
                      )}
                    >
                      <div className={cn(
                        'w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 mt-0.5',
                        notif.type === 'success' && 'bg-green-100 text-green-600',
                        notif.type === 'error' && 'bg-red-100 text-red-600',
                        notif.type === 'warning' && 'bg-amber-100 text-amber-600',
                        notif.type === 'info' && 'bg-blue-100 text-blue-600'
                      )}>
                        {notif.type === 'success' && <Check className="w-4 h-4" />}
                        {notif.type === 'error' && <X className="w-4 h-4" />}
                        {notif.type === 'warning' && <AlertTriangle className="w-4 h-4" />}
                        {notif.type === 'info' && <Info className="w-4 h-4" />}
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className={cn('font-medium text-sm', !notif.read && 'text-slate-900')}>{notif.title}</p>
                        <p className="text-xs text-slate-500 mt-0.5 truncate">{notif.message}</p>
                        <p className="text-xs text-slate-400 mt-1">{formatRelativeTime(notif.createdAt)}</p>
                      </div>
                      {!notif.read && <div className="w-2 h-2 bg-primary-500 rounded-full mt-2 flex-shrink-0" />}
                    </button>
                  ))
                )}
              </div>
              {notifications.length > 10 && (
                <div className="p-4 border-t border-slate-200 text-center">
                  <button className="text-sm text-primary-600 hover:text-primary-700">
                    View all notifications
                  </button>
                </div>
              )}
            </div>
          )}
        </div>

        {/* User Menu */}
        <div className="relative">
          <button
            onClick={() => setShowUserMenu(!showUserMenu)}
            className="flex items-center gap-2 p-1.5 rounded-lg hover:bg-slate-100 transition-colors"
            aria-label="User Menu"
          >
            <div className="w-8 h-8 rounded-full bg-primary-100 flex items-center justify-center">
              {user?.avatar ? (
                <img src={user.avatar} alt={user.name} className="w-8 h-8 rounded-full" />
              ) : (
                <span className="text-sm font-semibold text-primary-700">
                  {user?.name?.charAt(0) || 'U'}
                </span>
              )}
            </div>
            <span className="hidden md:block text-sm font-medium text-slate-700">{user?.name || 'Guest'}</span>
            <ChevronDown className="w-4 h-4 text-slate-500 hidden md:block" />
          </button>

          {showUserMenu && (
            <div className="absolute right-0 top-full mt-2 w-56 bg-white rounded-xl shadow-xl border border-slate-200 z-50 animate-scale-in">
              <div className="p-3 border-b border-slate-200">
                <p className="font-medium text-slate-900">{user?.name || 'Guest User'}</p>
                <p className="text-sm text-slate-500">{user?.email || 'Not signed in'}</p>
                <p className="text-xs text-slate-400 mt-1 capitalize">{user?.plan || 'free'} plan</p>
              </div>
              <nav className="py-2">
                {user ? (
                  <>
                    <NavItem label="Dashboard" icon={LayoutDashboard} href="/dashboard" />
                    <NavItem label="My Clients" icon={Users} href="/clients" />
                    <NavItem label="Billing" icon={CreditCard} href="/billing" />
                    <NavItem label="Analytics" icon={BarChart3} href="/analytics" />
                    <NavItem label="Settings" icon={Cog} href="/settings" />
                  </>
                ) : (
                  <>
                    <NavItem label="Sign In" icon={User} href="/signin" />
                    <NavItem label="Get Started" icon={Sparkles} href="/signup" />
                  </>
                )}
                <hr className="my-2 border-slate-200" />
                <button
                  onClick={handleSignOut}
                  className="w-full flex items-center gap-3 px-4 py-2.5 text-sm text-red-600 hover:bg-red-50 transition-colors"
                >
                  <LogOut className="w-5 h-5" />
                  Sign Out
                </button>
              </nav>
            </div>
          )}
        </div>
      </div>
    </header>
  );
}

import { MapPin } from 'lucide-react';

export default Header;